import {
  LogIn,
  LogOut,
  Monitor,
  Terminal,
  MonitorPlay,
  Shield,
  User,
  Eye,
  AppWindow,
  Trash2,
  RefreshCw,
  Share2,
  Key,
  GitBranch,
  Rocket,
  Upload,
  Link2,
  FileText,
  Bot,
  Bell,
  type LucideIcon,
} from 'lucide-react'
import type { AppSlot, AuditLogEvent } from './api'

export interface ActionDisplay {
  Icon: LucideIcon
  color: string
  bgColor: string
  label: string
}

// Source of truth for how each audit_log.action / platform_events.event_type
// is rendered. Keep aligned with FEATURE_AREAS in services/api/routers/analytics.py
// and ACTION_CONFIG referenced from admin/Activity.tsx.
const ACTION_CONFIG: Record<string, ActionDisplay> = {
  // Auth + workspace
  login: { Icon: LogIn, color: 'text-green-500', bgColor: 'bg-green-500/10', label: 'Login' },
  logout: { Icon: LogOut, color: 'text-gray-400', bgColor: 'bg-gray-500/10', label: 'Logout' },
  workspace_code: { Icon: Monitor, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Code Editor' },
  workspace_terminal: { Icon: Terminal, color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'Terminal' },
  workspace_desktop: { Icon: MonitorPlay, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Desktop' },
  workspace_session_create: { Icon: GitBranch, color: 'text-sky-500', bgColor: 'bg-sky-500/10', label: 'Session Created' },
  workspace_open: { Icon: Monitor, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Workspace Opened' },
  app_access_denied: { Icon: Shield, color: 'text-red-500', bgColor: 'bg-red-500/10', label: 'Access Denied' },
  sync_latest_changes_failed: { Icon: GitBranch, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Sync Failed' },
  notification_read: { Icon: Bell, color: 'text-slate-500', bgColor: 'bg-slate-500/10', label: 'Notification Read' },
  notifications_read_all: { Icon: Bell, color: 'text-slate-500', bgColor: 'bg-slate-500/10', label: 'Notifications Cleared' },
  admin_provision: { Icon: Shield, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10', label: 'Workspace Provisioned' },
  admin_impersonate: { Icon: User, color: 'text-orange-500', bgColor: 'bg-orange-500/10', label: 'Impersonated' },
  admin_stop_impersonating: { Icon: User, color: 'text-gray-500', bgColor: 'bg-gray-500/10', label: 'Stopped Impersonating' },
  admin_user_create: { Icon: User, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10', label: 'User Created (admin)' },
  admin_user_update: { Icon: User, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10', label: 'User Updated (admin)' },
  admin_user_delete: { Icon: User, color: 'text-red-500', bgColor: 'bg-red-500/10', label: 'User Deleted (admin)' },
  admin_container_restart: { Icon: RefreshCw, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Container Restarted (admin)' },
  admin_container_stop: { Icon: Rocket, color: 'text-gray-500', bgColor: 'bg-gray-500/10', label: 'Container Stopped (admin)' },
  admin_container_start: { Icon: Rocket, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', label: 'Container Started (admin)' },
  admin_settings_update: { Icon: Shield, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10', label: 'Settings Updated (admin)' },
  // App lifecycle
  app_visit: { Icon: Eye, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'App Visit' },
  app_create: { Icon: AppWindow, color: 'text-green-500', bgColor: 'bg-green-500/10', label: 'App Created' },
  app_clone: { Icon: AppWindow, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', label: 'App Cloned' },
  app_update: { Icon: AppWindow, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'App Updated' },
  app_delete: { Icon: Trash2, color: 'text-red-500', bgColor: 'bg-red-500/10', label: 'App Deleted' },
  app_restore: { Icon: AppWindow, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', label: 'App Restored' },
  app_permanent_delete: { Icon: Trash2, color: 'text-red-500', bgColor: 'bg-red-500/10', label: 'App Hard-Deleted' },
  app_start: { Icon: Rocket, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', label: 'App Started' },
  app_stop: { Icon: Rocket, color: 'text-gray-500', bgColor: 'bg-gray-500/10', label: 'App Stopped' },
  app_restart: { Icon: RefreshCw, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'App Restarted' },
  app_rebuild: { Icon: RefreshCw, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'App Rebuilt' },
  app_share: { Icon: Share2, color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'App Shared' },
  app_unshare: { Icon: Share2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'App Unshared' },
  app_share_permission_updated: { Icon: Share2, color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'Share Updated' },
  app_unsubscribe: { Icon: Share2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Unsubscribed' },
  access_request: { Icon: Share2, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Access Requested' },
  access_request_approved: { Icon: Share2, color: 'text-green-500', bgColor: 'bg-green-500/10', label: 'Access Approved' },
  access_request_denied: { Icon: Share2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Access Denied' },
  // Public-access admin-gated lifecycle (Phase v1.16+ feat/admin-gated-public-access)
  public_access_requested: { Icon: Share2, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Public Access Requested' },
  public_access_approved: { Icon: Share2, color: 'text-green-500', bgColor: 'bg-green-500/10', label: 'Public Access Approved' },
  public_access_rejected: { Icon: Share2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Public Access Rejected' },
  // Imports
  app_import_upload_received: { Icon: Upload, color: 'text-indigo-500', bgColor: 'bg-indigo-500/10', label: 'Import Uploaded' },
  app_import_upload_failed: { Icon: Upload, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Import Failed' },
  // OAuth integrations
  github_link: { Icon: Link2, color: 'text-slate-500', bgColor: 'bg-slate-500/10', label: 'GitHub Linked' },
  github_unlink: { Icon: Link2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'GitHub Unlinked' },
  gitlab_link: { Icon: Link2, color: 'text-orange-500', bgColor: 'bg-orange-500/10', label: 'GitLab Linked' },
  gitlab_unlink: { Icon: Link2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'GitLab Unlinked' },
  google_drive_link: { Icon: Link2, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Drive Linked' },
  google_drive_unlink: { Icon: Link2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Drive Unlinked' },
  google_drive_scopes_update: { Icon: Link2, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Drive Scopes Updated' },
  slack_link: { Icon: Link2, color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'Slack Linked' },
  slack_unlink: { Icon: Link2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Slack Unlinked' },
  slack_scopes_update: { Icon: Link2, color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'Slack Scopes Updated' },
  authdb_link: { Icon: Link2, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10', label: 'AuthDB Linked' },
  authdb_unlink: { Icon: Link2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'AuthDB Unlinked' },
  dynamics_link: { Icon: Link2, color: 'text-indigo-500', bgColor: 'bg-indigo-500/10', label: 'Dynamics Linked' },
  dynamics_unlink: { Icon: Link2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Dynamics Unlinked' },
  powerapps_link: { Icon: Link2, color: 'text-violet-500', bgColor: 'bg-violet-500/10', label: 'PowerApps Linked' },
  powerapps_unlink: { Icon: Link2, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'PowerApps Unlinked' },
  // Secrets
  app_secret_upsert: { Icon: Key, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Secret Set' },
  app_secret_delete: { Icon: Key, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Secret Deleted' },
  // Repo bindings
  sync_latest_changes: { Icon: GitBranch, color: 'text-sky-500', bgColor: 'bg-sky-500/10', label: 'Repo Synced' },
  share_for_review: { Icon: GitBranch, color: 'text-sky-500', bgColor: 'bg-sky-500/10', label: 'PR Published' },
  // Production deploys
  production_build_succeeded: { Icon: Rocket, color: 'text-green-500', bgColor: 'bg-green-500/10', label: 'Prod Build Succeeded' },
  production_build_failed: { Icon: Rocket, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Prod Build Failed' },
  production_build_rolled_back: { Icon: Rocket, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Prod Rolled Back' },
  // Templates
  template_promote: { Icon: FileText, color: 'text-indigo-500', bgColor: 'bg-indigo-500/10', label: 'Template Promoted' },
  template_update: { Icon: FileText, color: 'text-indigo-500', bgColor: 'bg-indigo-500/10', label: 'Template Updated' },
  template_delete: { Icon: FileText, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Template Deleted' },
  template_use: { Icon: FileText, color: 'text-indigo-500', bgColor: 'bg-indigo-500/10', label: 'Template Used' },
  // Chatbot
  chatbot_session_started: { Icon: Bot, color: 'text-teal-500', bgColor: 'bg-teal-500/10', label: 'Chatbot Started' },
}

const FALLBACK: ActionDisplay = {
  Icon: User,
  color: 'text-muted-foreground',
  bgColor: 'bg-muted',
  label: '',
}

export function getActionDisplay(action: string): ActionDisplay {
  const cfg = ACTION_CONFIG[action]
  if (cfg) return cfg
  return {
    ...FALLBACK,
    label: action.replace(/_/g, ' '),
  }
}

export function actionFilterOptions(): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'All Actions' },
    ...Object.entries(ACTION_CONFIG)
      .map(([value, cfg]) => ({ value, label: cfg.label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ]
}

const SLOT_STYLES: Record<AppSlot, string> = {
  prod: 'bg-green-500/15 text-green-600 dark:text-green-400',
  dev: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  branch: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
}

export function slotBadgeClass(slot: AppSlot): string {
  return SLOT_STYLES[slot] ?? 'bg-muted text-muted-foreground'
}

export function slotLabel(slot: AppSlot, details?: Record<string, unknown> | null): string {
  if (slot === 'branch') {
    const branch = details && typeof details === 'object' ? (details as Record<string, unknown>).branch : null
    return typeof branch === 'string' && branch ? `branch:${branch}` : 'branch'
  }
  return slot
}

export function eventDisplayName(event: AuditLogEvent): string {
  if (event.user_name) return event.user_name
  if (event.email) return event.email.split('@')[0]
  return 'anonymous'
}

/**
 * True when an event represents an anonymous visitor — currently only
 * fired for public-app visits where the proxy had no session to tie
 * the request back to a user. The visit_tracker stamps the row with
 * ``metadata.anonymous=true``; we also accept "no user_id" as a
 * fallback so admin tooling spots rows even if the metadata flag is
 * missing for any reason.
 */
export function isAnonymousEvent(event: AuditLogEvent): boolean {
  if (event.action !== 'app_visit') return false
  if (event.user_id != null) return false
  if (event.details && typeof event.details === 'object') {
    const flag = (event.details as Record<string, unknown>).anonymous
    if (flag === true) return true
  }
  // Fallback: a visit row with no user_id is by definition anonymous,
  // even if the metadata flag is missing on older rows.
  return true
}

/**
 * Convert a raw event.details JSON into a short human-readable hint that
 * supplements the action label. Returns null when there's nothing useful
 * to show. Per-action shapes are explicit — a generic JSON dump would be
 * noisy and leak internals.
 *
 * Keep aligned with the audit_log call sites in services/{api,router}/.
 * If a new action carries non-trivial context in details, add a branch
 * here so the activity feed actually surfaces it.
 */
export function eventDetailHint(event: AuditLogEvent): string | null {
  const details = event.details
  if (!details || typeof details !== 'object') return null
  const d = details as Record<string, unknown>

  switch (event.action) {
    case 'app_update': {
      // apps.py logs details={"fields_changed": [...]} — the UI used to
      // show a bare "App Updated" tag with no hint which field changed,
      // which made "I just toggled link-sharing" look identical to
      // renaming the app.
      //
      // When access_level transitions, apps.py also writes
      // `access_level_from` and `access_level_to`. Surface them as a
      // concrete English line ("Made public", "Reverted public app to
      // personal", "Changed sharing: X → Y") instead of the bare
      // "fields: access_level" — the latter buried the most operationally
      // interesting transition (public toggle) in implementation jargon.
      const from = typeof d.access_level_from === 'string' ? d.access_level_from : null
      const to = typeof d.access_level_to === 'string' ? d.access_level_to : null
      if (from && to && from !== to) {
        if (to === 'public') return 'Made public (anonymous access)'
        if (from === 'public') return `Reverted public app to ${to}`
        return `Changed sharing: ${from} → ${to}`
      }
      const fields = Array.isArray(d.fields_changed) ? (d.fields_changed as unknown[]) : []
      const strs = fields.filter((f): f is string => typeof f === 'string')
      return strs.length ? `fields: ${strs.join(', ')}` : null
    }
    case 'app_share':
    case 'app_share_permission_updated': {
      const email = typeof d.share_email === 'string' ? d.share_email : null
      const perm = typeof d.permission === 'string' ? d.permission : null
      const parts = [email, perm].filter(Boolean)
      return parts.length ? parts.join(' · ') : null
    }
    case 'app_unshare': {
      return typeof d.share_email === 'string' ? d.share_email : null
    }
    case 'app_secret_upsert':
    case 'app_secret_delete': {
      return typeof d.key === 'string' ? `key: ${d.key}` : null
    }
    case 'production_build_succeeded':
    case 'production_build_failed':
    case 'production_build_rolled_back': {
      return typeof d.build_id === 'number' ? `build #${d.build_id}` : null
    }
    case 'public_access_requested': {
      return typeof d.has_justification === 'boolean' && d.has_justification
        ? 'with justification'
        : null
    }
    case 'public_access_approved':
    case 'public_access_rejected': {
      // Router /admin/public-access-requests/{id}/{approve|reject} writes
      // details={"request_id", "review_note", "requester_user_id"}. Surface
      // the optional review note so admins see why the decision was made
      // when scanning the audit feed.
      return typeof d.review_note === 'string' && d.review_note
        ? `note: ${d.review_note}`
        : null
    }
    default:
      return null
  }
}
